import { apiErrorSchema } from '@amai/contracts';
import { ApiRequestError, NetworkError } from './errors.js';
import { clearTokens, getAccessToken, getRefreshToken, setTokens } from './session.js';

/**
 * Client HTTP do admin.
 *
 * Os tipos das respostas vêm dos contratos (`@amai/contracts`) — os mesmos que
 * a API usa para validar. `src/lib/api-types.ts` é gerado do OpenAPI e serve de
 * conferência: se uma rota mudar de forma sem o contrato acompanhar, o
 * typecheck acusa.
 */

const BASE_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Rotas de login/refresh não devem tentar renovar a sessão. */
  skipRefresh?: boolean;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${BASE_URL}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toApiError(response: Response): Promise<ApiRequestError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  const parsed = apiErrorSchema.safeParse(payload);
  if (parsed.success) return new ApiRequestError(response.status, parsed.data.error);

  // A API sempre responde no envelope; se chegou algo diferente (proxy, gateway),
  // ainda assim traduzimos por código HTTP em vez de mostrar o corpo cru.
  const fallback =
    response.status === 401
      ? ('UNAUTHENTICATED' as const)
      : response.status === 403
        ? ('FORBIDDEN' as const)
        : response.status === 404
          ? ('NOT_FOUND' as const)
          : response.status === 429
            ? ('RATE_LIMITED' as const)
            : ('INTERNAL' as const);
  return new ApiRequestError(response.status, { code: fallback, message: '' });
}

/**
 * Resultado da renovação. Falhar tem dois sentidos bem diferentes:
 * - `recusada`: o servidor recusou o refresh token (vencido, revogado). A
 *   sessão ACABOU — os tokens são apagados e o 401 original segue adiante;
 * - `indisponivel`: o servidor não pôde responder agora (429, 5xx, rede).
 *   A sessão continua valendo, e quem pediu recebe ESSE erro, não um 401 —
 *   senão a tela trataria "servidor ocupado" como "sessão encerrada".
 */
type Renovacao =
  { ok: true } | { ok: false; recusada: true } | { ok: false; recusada: false; erro: Error };

/** Uma renovação por vez: dez requisições em paralelo não geram dez refreshes. */
let refreshInFlight: Promise<Renovacao> | null = null;

async function refreshSession(): Promise<Renovacao> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return { ok: false, recusada: true };

  refreshInFlight ??= (async (): Promise<Renovacao> => {
    try {
      const response = await fetch(buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!response.ok) {
        // Só 4xx (menos 429) é o servidor RECUSANDO o token.
        if (response.status >= 400 && response.status < 500 && response.status !== 429) {
          clearTokens();
          return { ok: false, recusada: true };
        }
        return { ok: false, recusada: false, erro: await toApiError(response) };
      }
      const body = (await response.json()) as {
        tokens: { accessToken: string; refreshToken: string; expiresIn: number };
      };
      setTokens(body.tokens);
      return { ok: true };
    } catch {
      return { ok: false, recusada: false, erro: new NetworkError() };
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, signal, skipRefresh } = options;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (body !== undefined) headers['content-type'] = 'application/json';
    const token = getAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;

    try {
      return await fetch(buildUrl(path, query), {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new NetworkError();
    }
  };

  let response = await send();

  // 401 com refresh disponível: renova uma vez e repete a requisição original.
  if (response.status === 401 && !skipRefresh && getRefreshToken()) {
    const renovacao = await refreshSession();
    if (renovacao.ok) response = await send();
    else if (!renovacao.recusada) throw renovacao.erro;
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Upload e download passam por aqui, e não pelo `api` normal, porque o corpo
 * é binário: multipart na subida, stream na descida. Serializar como JSON
 * transformaria um PDF de 3 MB em 4 MB de base64 por nada.
 */
export async function upload<T>(path: string, form: FormData): Promise<T> {
  const send = () => {
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) headers.authorization = `Bearer ${token}`;
    // O content-type NÃO é definido aqui: o navegador precisa montar o
    // boundary do multipart sozinho.
    return fetch(buildUrl(path), { method: 'POST', headers, body: form }).catch(() => {
      throw new NetworkError();
    });
  };

  let response = await send();
  if (response.status === 401 && getRefreshToken()) {
    const renovacao = await refreshSession();
    if (renovacao.ok) response = await send();
    else if (!renovacao.recusada) throw renovacao.erro;
  }
  if (!response.ok) throw await toApiError(response);
  return (await response.json()) as T;
}

/** Baixa o arquivo com o token no cabeçalho e entrega ao navegador. */
export async function download(path: string, filename: string): Promise<void> {
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.authorization = `Bearer ${token}`;

  const response = await fetch(buildUrl(path), { headers }).catch(() => {
    throw new NetworkError();
  });
  if (!response.ok) throw await toApiError(response);

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(objectUrl);
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
